import { S3Client, PutObjectCommand, PutObjectCommandInput, ListObjectsV2Command } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';

interface DOSpacesConfig {
  endpoint: string;
  region: string;
  accessKeyId: string;
  secretAccessKey: string;
  bucket: string;
}

interface UploadResult {
  success: boolean;
  fileName?: string;
  filePath?: string;
  fileSize?: number;
  mimeType?: string;
  error?: string;
}

interface FileValidation {
  isValid: boolean;
  errors: string[];
}

class DigitalOceanSpacesService {
  private client: S3Client;
  private config: DOSpacesConfig;

  constructor() {
    this.config = {
      endpoint: import.meta.env.VITE_DO_SPACES_ENDPOINT || '',
      region: import.meta.env.VITE_DO_SPACES_REGION || 'nyc3',
      accessKeyId: import.meta.env.VITE_DO_SPACES_ACCESS_KEY || '',
      secretAccessKey: import.meta.env.VITE_DO_SPACES_SECRET_KEY || '',
      bucket: import.meta.env.VITE_DO_SPACES_BUCKET || ''
    };

    if (!this.validateConfig()) {
      console.error('Digital Ocean Spaces configuration is incomplete');
    }

    // If endpoint already includes bucket name, remove it for S3Client
    let clientEndpoint = this.config.endpoint;
    if (clientEndpoint.includes(this.config.bucket)) {
      // Extract the base endpoint without bucket name
      // e.g., https://lbtiling.sfo3.digitaloceanspaces.com -> https://sfo3.digitaloceanspaces.com
      clientEndpoint = clientEndpoint.replace(`${this.config.bucket}.`, '');
    }

    this.client = new S3Client({
      endpoint: clientEndpoint,
      region: this.config.region,
      credentials: {
        accessKeyId: this.config.accessKeyId,
        secretAccessKey: this.config.secretAccessKey,
      },
      forcePathStyle: false, // Digital Ocean Spaces uses subdomain style
      tls: true,
      // Add additional configuration for better browser compatibility
      requestHandler: {
        requestTimeout: 30000, // 30 second timeout
        httpsAgent: undefined, // Let browser handle HTTPS
      },
    });
  }

  private validateConfig(): boolean {
    const required = ['endpoint', 'accessKeyId', 'secretAccessKey', 'bucket'];
    
    // Debug logging to see what values are loaded
    console.log('DO Spaces Config Debug:', {
      endpoint: this.config.endpoint ? '✓ SET' : '✗ MISSING',
      region: this.config.region ? '✓ SET' : '✗ MISSING', 
      accessKeyId: this.config.accessKeyId ? '✓ SET' : '✗ MISSING',
      secretAccessKey: this.config.secretAccessKey ? '✓ SET' : '✗ MISSING',
      bucket: this.config.bucket ? '✓ SET' : '✗ MISSING'
    });
    
    return required.every(key => {
      const value = this.config[key as keyof DOSpacesConfig];
      const isValid = !!value && value.trim() !== '';
      if (!isValid) {
        console.error(`Missing or empty config value for: ${key}`);
      }
      return isValid;
    });
  }

  validateResumeFile(file: File): FileValidation {
    const errors: string[] = [];
    
    // Check file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      errors.push('File size must be less than 2MB');
    }

    // Check file type
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      errors.push('File must be PDF, DOC, or DOCX format');
    }

    // Check file name length
    if (file.name.length > 100) {
      errors.push('Filename must be less than 100 characters');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private generateUniqueFileName(originalName: string): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const extension = originalName.substring(originalName.lastIndexOf('.'));
    const baseName = originalName.substring(0, originalName.lastIndexOf('.')).replace(/[^a-zA-Z0-9]/g, '_');
    
    return `${timestamp}_${randomString}_${baseName}${extension}`;
  }

  async uploadResume(file: File, applicantName: string): Promise<UploadResult> {
    try {
      // Validate configuration
      if (!this.validateConfig()) {
        return {
          success: false,
          error: 'Digital Ocean Spaces configuration is incomplete'
        };
      }

      // Validate file
      const validation = this.validateResumeFile(file);
      if (!validation.isValid) {
        return {
          success: false,
          error: validation.errors.join(', ')
        };
      }

      // Generate unique filename
      const fileName = this.generateUniqueFileName(file.name);
      const key = `resumes/${fileName}`;

      // Prepare upload parameters
      const uploadParams: PutObjectCommandInput = {
        Bucket: this.config.bucket,
        Key: key,
        Body: file,
        ContentType: file.type,
        ACL: 'private', // Private access - only accessible with credentials
        Metadata: {
          'applicant-name': applicantName.replace(/[^a-zA-Z0-9\s]/g, ''), // Clean metadata
          'upload-timestamp': new Date().toISOString(),
          'original-filename': file.name
        }
      };

      // Use multipart upload for better reliability
      const upload = new Upload({
        client: this.client,
        params: uploadParams,
        queueSize: 4, // Optional concurrency configuration
        partSize: 1024 * 1024 * 5, // 5MB parts
        leavePartsOnError: false, // Auto-cleanup on failure
      });

      // Monitor upload progress (optional)
      upload.on('httpUploadProgress', (progress) => {
        console.log(`Upload progress: ${Math.round((progress.loaded || 0) / (progress.total || 1) * 100)}%`);
      });

      await upload.done();

      // Generate the public URL for the uploaded file
      const fileUrl = `${this.config.endpoint}/${this.config.bucket}/${key}`;

      return {
        success: true,
        fileName: file.name,
        filePath: fileUrl, // Return the full URL as expected by backend
        fileSize: file.size,
        mimeType: file.type
      };

    } catch (error: any) {
      console.error('Error uploading resume to Digital Ocean Spaces:', error);
      
      let errorMessage = 'Failed to upload resume';
      
      if (error.name === 'NoSuchBucket') {
        errorMessage = 'Storage bucket not found';
      } else if (error.name === 'AccessDenied') {
        errorMessage = 'Access denied to storage service';
      } else if (error.name === 'NetworkingError') {
        errorMessage = 'Network error occurred during upload';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * List files from gallery folder with pagination support
   * @param maxKeys - Maximum number of files to return per request (default: 50)
   * @param continuationToken - Token for fetching next page of results
   * @param sortOrder - Sort order: 'asc' (alphabetical A-Z) or 'desc' (reverse Z-A). Default: 'asc'
   *
   * Note: Returns thumbnail URLs for efficient gallery loading, with medium and original URLs available.
   * Files are loaded from gallery/thumbs/ folder for optimal performance.
   */
  async listGalleryFiles(
    maxKeys: number = 50,
    continuationToken?: string,
    sortOrder: 'asc' | 'desc' = 'asc'
  ): Promise<{
    success: boolean;
    files: Array<{
      name: string;
      path: string;
      thumbPath: string;
      mediumPath: string;
      originalPath: string;
      mediaType: 'image' | 'video'
    }>;
    hasMore: boolean;
    nextToken?: string;
    error?: string
  }> {
    try {
      if (!this.validateConfig()) {
        return { success: false, files: [], hasMore: false, error: 'Configuration validation failed' };
      }

      // List files from the thumbs folder (subfolder structure)
      const command = new ListObjectsV2Command({
        Bucket: this.config.bucket,
        Prefix: 'gallery/thumbs/',
        MaxKeys: maxKeys,
        ContinuationToken: continuationToken,
      });

      const response = await this.client.send(command);

      if (!response.Contents || response.Contents.length === 0) {
        return { success: true, files: [], hasMore: false };
      }

      // Helper function to build URL
      const buildUrl = (path: string): string => {
        if (this.config.endpoint.includes(this.config.bucket)) {
          return `${this.config.endpoint}/${path}`;
        } else {
          const cleanEndpoint = this.config.endpoint.replace('https://', '');
          return `https://${this.config.bucket}.${cleanEndpoint}/${path}`;
        }
      };

      let files = response.Contents
        .filter(item => item.Key && item.Key !== 'gallery/thumbs/') // Exclude folder itself
        .map(item => {
          const key = item.Key!;
          const fileName = key.split('/').pop() || key;

          // Build URLs for all three sizes using subfolder structure
          const thumbPath = buildUrl(`gallery/thumbs/${fileName}`);
          const mediumPath = buildUrl(`gallery/medium/${fileName}`);
          const originalPath = buildUrl(`gallery/original/${fileName}`);

          // Determine media type from extension
          const extension = fileName.toLowerCase().split('.').pop();
          const videoExtensions = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
          const mediaType: 'image' | 'video' = videoExtensions.includes(extension || '') ? 'video' : 'image';

          return {
            name: fileName,
            path: thumbPath, // Default path is thumbnail for backward compatibility
            thumbPath,
            mediumPath,
            originalPath,
            mediaType,
            key // Keep key for sorting
          };
        });

      // Apply sorting (S3 returns in alphabetical order by default, but we can reverse it)
      if (sortOrder === 'desc') {
        files = files.reverse();
      }

      // Remove the key from the final output
      const cleanedFiles = files.map(({ key, ...rest }) => rest);

      return {
        success: true,
        files: cleanedFiles,
        hasMore: response.IsTruncated || false,
        nextToken: response.NextContinuationToken
      };

    } catch (error: any) {
      console.error('Error listing gallery files from Digital Ocean Spaces:', error);

      let errorMessage = 'Failed to list gallery files';
      if (error.name === 'NoSuchBucket') {
        errorMessage = 'Storage bucket not found';
      } else if (error.name === 'AccessDenied') {
        errorMessage = 'Access denied to storage service';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, files: [], hasMore: false, error: errorMessage };
    }
  }

  // Method to test the connection (useful for debugging)
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.validateConfig()) {
        return { success: false, error: 'Configuration validation failed' };
      }

      console.log('Testing Digital Ocean Spaces connection...');
      console.log('Endpoint:', this.config.endpoint);
      console.log('Region:', this.config.region);
      console.log('Bucket:', this.config.bucket);

      // Try to create a simple test object
      const command = new PutObjectCommand({
        Bucket: this.config.bucket,
        Key: 'connection-test.txt',
        Body: 'Connection test successful',
        ACL: 'private'
      });

      await this.client.send(command);
      console.log('Digital Ocean Spaces connection test successful');
      return { success: true };
    } catch (error: any) {
      console.error('Digital Ocean Spaces connection test failed:', error);
      
      let errorMessage = 'Connection test failed';
      if (error.name === 'NetworkingError' || error.message?.includes('Failed to fetch')) {
        errorMessage = 'Network error - check CORS settings and endpoint URL';
      } else if (error.name === 'CredentialsProviderError' || error.name === 'SignatureDoesNotMatch') {
        errorMessage = 'Invalid credentials - check access key and secret';
      } else if (error.name === 'NoSuchBucket') {
        errorMessage = 'Bucket not found - verify bucket name';
      } else if (error.name === 'AccessDenied') {
        errorMessage = 'Access denied - check bucket permissions';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  }
}

// Export singleton instance
export const digitalOceanSpacesService = new DigitalOceanSpacesService();
export type { UploadResult, FileValidation };